"use client";
import React, { useEffect, useRef } from "react";
import { Modal, Tabs } from "antd";
import { useAtom } from "jotai";
import { useIntl } from "react-intl";
import LegalContent from "./LegalContent";
import {
  legalDocFromPath,
  legalDocModalAtom,
  TLegalDoc,
} from "../../Orchestrator/Recoils/LegalDocModalAtom";
import "../../app/terms-and-conditions/page.css";
import "./LegalDocModal.css";

const DOC_TITLE_IDS: Record<TLegalDoc, string> = {
  "privacy-policy": "privacyPolicy",
  "cookie-policy": "cookiePolicy",
  "terms-of-use": "termsOfUse",
  "passenger-charter": "passengerCharter",
};

/* Same tab set (and order) as the /terms-and-conditions page. */
const TERMS_TABS: TLegalDoc[] = [
  "passenger-charter",
  "terms-of-use",
  "privacy-policy",
];

/**
 * Popup viewer for the legal documents. Mounted once in ClientProvider and
 * opened via openLegalDoc(); reuses LegalContent and the terms page styles so
 * the popup shows exactly what the standalone pages (and the mobile WebView)
 * show.
 */
const LegalDocModal = () => {
  const intl = useIntl();
  const [doc, setDoc] = useAtom(legalDocModalAtom);
  const bodyRef = useRef<HTMLDivElement>(null);
  // One scroll container per terms tab. All terms panels are force-rendered so
  // every document loads when the modal opens; switching tabs then only toggles
  // visibility instead of re-fetching. This ref map lets us reset the scroll of
  // whichever document is shown.
  const tabBodyRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const active =
      doc && TERMS_TABS.includes(doc) ? tabBodyRefs.current[doc] : bodyRef.current;
    active?.scrollTo({ top: 0 });
  }, [doc]);

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href") || "";

    if (href.startsWith("#")) {
      // Table-of-contents links scroll inside the popup, not the page
      // (modified clicks included: a new tab on "#section" is never useful).
      e.preventDefault();
      const id = decodeURIComponent(href.slice(1));
      // The handler is bound to each document's scroll container, so
      // e.currentTarget is the visible doc's body — works for every terms tab
      // (which now use per-tab refs) and the standalone cookie-policy body.
      e.currentTarget
        .querySelector(`[id="${CSS.escape(id)}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    // Modified clicks (ctrl/cmd/shift -> new tab) keep browser behaviour.
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

    const crossDoc = legalDocFromPath(href);
    if (crossDoc) {
      // Cross-references between the documents swap the popup content.
      e.preventDefault();
      setDoc(crossDoc);
      return;
    }

    if (/^https?:\/\//.test(href) || href.startsWith("/")) {
      // External references and other in-app pages (e.g. /contact) open in
      // a new tab so the booking session behind the popup is never replaced.
      e.preventDefault();
      window.open(href, "_blank", "noopener,noreferrer");
    }
    // mailto:/tel: links keep the browser default behaviour.
  };

  const isTermsDoc = doc !== null && TERMS_TABS.includes(doc);
  /* Mirror the standalone pages: the T&C documents share one title and a tab
     bar to switch between them; the cookie policy stands alone. */
  const titleId = isTermsDoc ? "termsAndConditions" : doc ? DOC_TITLE_IDS[doc] : null;

  const docBody = doc && (
    <div
      ref={bodyRef}
      className="legal-doc-modal-body"
      onClick={handleContentClick}
    >
      <LegalContent src={`/legal/${doc}.html`} />
    </div>
  );

  return (
    <Modal
      open={doc !== null}
      onCancel={() => setDoc(null)}
      footer={null}
      centered
      width="min(1000px, 94vw)"
      /* Above the cookie consent banner (z-index 9999). */
      zIndex={10010}
      destroyOnHidden
      className="legal-doc-modal"
      title={titleId ? intl.formatMessage({ id: titleId }) : ""}
    >
      {isTermsDoc ? (
        <Tabs
          activeKey={doc}
          onChange={(key) => setDoc(key as TLegalDoc)}
          className="tnc-tabs legal-doc-modal-tabs"
          /* The document renders inside the active tab panel so the
             tablist/tabpanel relationship is real for assistive tech. */
          items={TERMS_TABS.map((tab) => ({
            key: tab,
            label: intl.formatMessage({ id: DOC_TITLE_IDS[tab] }),
            // Force-render every panel so all terms documents load when the
            // modal opens; switching tabs then only toggles visibility instead
            // of re-fetching the document each time.
            forceRender: true,
            children: (
              <div
                ref={(el) => {
                  tabBodyRefs.current[tab] = el;
                }}
                className="legal-doc-modal-body"
                onClick={handleContentClick}
              >
                <LegalContent src={`/legal/${tab}.html`} />
              </div>
            ),
          }))}
        />
      ) : (
        docBody
      )}
    </Modal>
  );
};

export default LegalDocModal;
